import InfoBalloon from "@/components/Parlor/ParlorDetail/InfoBalloon";
import numberToLocalString from "@/utils/numberToLocalString";
import { Box } from "@chakra-ui/react";
import { ColorIcon, colors, customEvent, HLine, HStack, NextLink, VStack } from "goi_common";
import { useRouter } from "next/router";

import { ReactNode, useMemo } from "react";

const SANGJO_PEOPLE_INFO: { [type: string]: string } = {
  무빈소: "(조문객 없음)",
  가족장: "(조문객 50명 내외)",
  일반장: "(조문객 200명 이상)",
};

interface Props {
  name: string;
  sidoName: string;
  sido: string;
  sigunguName?: string;
  sigungu?: string;
  parlors: Array<any>;
  type: "전체" | "무빈소장" | "가족장" | "일반장";
  hasSigunguParlor: boolean;
  isSigungu: boolean;
}
function ParlorServiceSummary(props: Props) {
  const { name, sidoName, sido, sigunguName, sigungu, parlors, type, hasSigunguParlor, isSigungu } =
    props;
  const router = useRouter();

  const items = useMemo(() => {
    if (type === "전체") {
      return [
        {
          value: "무빈소",
          type: "무빈소장례",
          price:
            parlors.reduce((prev, curr) => {
              return curr.expected_charge_sum["무빈소"] + prev;
            }, 0) / parlors.length,
        },
        {
          value: "가족장",
          type: "가족장",
          price:
            parlors.reduce((prev, curr) => {
              return curr.expected_charge_sum["가족장"] + prev;
            }, 0) / parlors.length,
        },
        {
          value: "일반장",
          type: "일반장",
          price:
            parlors.reduce((prev, curr) => {
              return curr.expected_charge_sum["일반장"] + prev;
            }, 0) / parlors.length,
        },
      ];
    } else {
      return [
        {
          value: type,
          type: type === "무빈소장" ? "무빈소장례" : type,
          price:
            parlors.reduce((prev, curr) => {
              return curr.expected_charge_sum[type === "무빈소장" ? "무빈소" : type] + prev;
            }, 0) / parlors.length,
        },
      ];
    }
  }, [type, sido, sigungu, name, type]);

  const showSummaryItem = !isSigungu || (isSigungu && hasSigunguParlor);

  return (
    <VStack>
      <Box
        padding="8px 16px"
        borderRadius="10px"
        background={colors.warmGray50}
        border={`1px solid ${colors.gray200}`}
        margin="12px 20px 24px"
      >
        {showSummaryItem ? (
          items.map((item) => (
            <SummaryItem
              key={item.value}
              title={`${name} ${item.type} 평균 비용/이용금액`}
              price={item.price}
              description={SANGJO_PEOPLE_INFO[item.value]}
              button={
                type === "전체" ? (
                  <NextLink
                    href={`/parlors/region/service/${
                      sigunguName ? `${sidoName}-${sigunguName}` : sidoName
                    }/${item.value === "무빈소" ? "무빈소장" : item.value}/`}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      className="caption_11_b gray600"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        customEvent({
                          newGtm: {
                            path: "parlor_region",
                            trigger_and_target: "service_by_chip-uv_parlor_region_service",
                          },
                        });
                      }}
                    >
                      가격 순으로 보기
                      <ColorIcon
                        name="arrow_right_small"
                        color={colors.gray500}
                        width="12px"
                        height="12px"
                      />
                    </Box>
                  </NextLink>
                ) : null
              }
            />
          ))
        ) : (
          <VStack
            className="body_14_m gray600"
            gap="9px"
            css={{
              padding: "10px 0",
            }}
          >
            <p>
              {sigunguName}에 주소를 둔 장례식장이 검색되지 않아 평균 비용을 계산할 수 없어요.{" "}
              {sidoName}지역 장례식장 비용을 확인해보세요.
            </p>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              className="subtitle4 gray700"
              style={{ cursor: "pointer" }}
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "parlor_region",
                    trigger_and_target: "sigungu_parlors-uv_sido_parlors",
                  },
                });
                router.push({
                  href: router.asPath,
                  query: {
                    ...router.query,
                    code: sidoName,
                  },
                });
              }}
            >
              {sidoName} 장례식장 전체보기
              <ColorIcon
                name="arrow_right_small"
                color={colors.gray700}
                width="12px"
                height="12px"
              />
            </Box>
          </VStack>
        )}
      </Box>
      <InfoBalloon
        event={{
          newGtm: {
            path: "parlor_region",
            trigger_and_target: "check_discount-uv_match_request",
          },
        }}
      />
    </VStack>
  );
}
export default ParlorServiceSummary;

const SummaryItem = ({
  title,
  price,
  description,
  button,
}: {
  title: string;
  price: number;
  description: string;
  button?: ReactNode;
}) => {
  return (
    <>
      <HStack alignItems="center" justifyContent="space-between" width="100%" padding="12px 0">
        <VStack gap={0} margin={0} width="100%">
          <Box as="h2" className="button2 gray500 text-left">
            {title}
          </Box>
          <HStack alignItems="center" justifyContent="space-between">
            <HStack className="gray900" alignItems="center" gap="1px">
              <span className="subtitle_18_b">{numberToLocalString(Math.ceil(price))}</span>
              <span className="subtitle_16_b">원</span>
              <span className="caption_11_m gray500">{description}</span>
            </HStack>
            {button}
          </HStack>
        </VStack>
      </HStack>
      <HLine color={colors.gray100} width="100%" height="1px" />
    </>
  );
};
